import moment from 'jalali-moment';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CloseCircleOutlined } from '@ant-design/icons';
import { Alert, App, Col, Popconfirm, Row } from 'antd';
import { Routes } from '@/routes';
import { ColumnBuilder } from '@/utils';
import { useUserContext } from '@/context';
import { useDevice, useDormitoryGroupManagement, useThemeToken } from '@/hooks';
import { AppButton, Flex, InputPlus, LogTable, LogTableColumn } from '@/components';
import { dormitoryInviteStatus, dormitorySolitaryStatus } from '../helper';
import { BoxDescription, BoxIcon } from './DormitoryUIKit';
import MobileMembersList from './MobileMembersList';

export interface DormitoryRegisterGroupManagementProps {
  group: DormitoryGroup;
  onRefresh?: () => void;
  validEntrance: DormitoryValidEntranceResponse;
}

export default function DormitoryRegisterGroupManagement({
  group,
  onRefresh,
  validEntrance,
}: DormitoryRegisterGroupManagementProps) {

  const [t] = useTranslation(['common', 'dormitory']);

  const columns = useMemo(() => ColumnBuilder.create([
    { key: 'username', title: t('common:labels.username'), align: 'center' },
    { key: 'firstName', title: t('common:labels.firstname'), align: 'center' },
    { key: 'lastName', title: t('common:labels.lastname'), align: 'center' },
    { key: 'type', title: t('common:labels.title'), align: 'center' },
    { key: 'date', title: t('dormitory:labels.register-date'), align: 'center' },
    { key: 'status', title: t('common:labels.status'), align: 'center' },
    { key: 'actions', title: ' ', align: 'center' },
  ]).build() as LogTableColumn[], [t]);

  const { isMobile } = useDevice();
  const { profile } = useUserContext();
  const { colorPrimary } = useThemeToken();
  const { removeUserFromGroup } = useDormitoryGroupManagement();

  const isHead = group.head != null && group?.head?.studentNumber === profile?.student?.studentNumber;

  const rows = [group.head, ...(group.members ?? [])].map((member, i) => ({
    username: member.studentNumber,
    firstName: member.firstName,
    lastName: member.lastName,
    type: i === 0 ? t('common:labels.team-leader') : t('common:labels.member'),
    date: <div className="ltr text-end">{moment.from(member.date, 'en', 'YYYY-MM-DD HH:mm:ss').format('jYYYY/jMM/jDD HH:mm')}</div>,
    statusRaw: member.status,
    status: group.solitary ? dormitorySolitaryStatus(member?.status, t) : dormitoryInviteStatus(member?.status, t),
    actions: (
      group.status !== 'FINAL' && (!group.transferStatus && isHead && i !== 0) ? (
        <Flex align="center">
          <Popconfirm
            okType="danger"
            okText={t('common:labels.delete')}
            title={t('common:messages.confirm-remove')}
            onConfirm={() => removeUserFromGroup(member.studentNumber, false).then(onRefresh)}>
            <CloseCircleOutlined className="text-red-600 text-lg cursor-pointer" />
          </Popconfirm>
        </Flex>
      ) : <></>
    )
  }));

  const me = rows.find(r => r.username === profile?.student?.studentNumber);
  const mobile = isMobile();

  const solitaryDiff = group?.solitary != validEntrance.solitaryRegistration;
  const showRemaining = !group?.solitary && (typeof group?.roomSize === 'number' && (group?.roomSize - rows.length) !== 0);

  return (
    <Row gutter={[16, 16]}>
      <Col md={7} xs={24}>
        <Alert
          style={{ border: `1px solid ${colorPrimary}` }}
          className="text-sm md:p-4 p-3 mx-1 leading-relaxed text-justify bg-[#E3D5CA]"
          message={(
            <div style={{ color: colorPrimary }} className="font-normal flex flex-col">
              <div className="relative flex flex-col items-center">
                {!mobile &&
                  <BoxIcon
                    groupStatus={group?.status}
                    memberStatus={me?.statusRaw}
                  />
                }
                <BoxDescription
                  groupStatus={group?.status}
                  memberStatus={me?.statusRaw}
                />
                {(showRemaining) &&
                  <hr className="w-full py-2" style={{ border: 0, borderBottom: '1px solid #77604E' }} />
                }
                {showRemaining ?
                  <Flex gap={2} align="center" justify="flex-start" className="w-full mt-2 font-bold text-[#333]">
                    <span className="text-sm">{t('dormitory:messages:remain-until-select-room')}:</span>
                    <span className="text-sm ms-1 min-w-[60px]">{group?.roomSize - rows.length} {t('common:labels.person')}</span>
                  </Flex> : ''
                }
              </div>
            </div>
          )}
        />
      </Col>
      <Col md={17} xs={24}>
        <Row className="px-3" gutter={[16, 0]}>
          <ManagementButtons
            group={group}
            isHead={isHead}
            rows={rows}
            onRefresh={onRefresh}
            solitaryDiff={solitaryDiff}
          />
          <Col className={group?.status === 'FINAL' ? 'mt-2' : 'mt-4'} span={24}>
            {isMobile() ? <MobileMembersList rows={rows} /> :
              <LogTable rows={rows} scroll={{ x: 'auto' }} columns={columns} />
            }
          </Col>
        </Row>
      </Col>
    </Row>
  );
}

interface ManagementButtonsProps {
  rows: any[];
  isHead: boolean;
  group: DormitoryGroup;
  solitaryDiff: boolean;
  onRefresh?: () => void;
}

function ManagementButtons({
  group,
  isHead,
  rows,
  onRefresh,
  solitaryDiff,
}: ManagementButtonsProps) {

  const navigate = useNavigate();
  const { isMobile } = useDevice();
  const { modal, message } = App.useApp();
  const { profile } = useUserContext();
  const { colorPrimary } = useThemeToken();
  const [t] = useTranslation(['common', 'dormitory']);

  const {
    invite,
    accepting,
    acceptInvite,
    rejecting,
    rejectInvite,
    removeRoom,
    removeGroup
  } = useDormitoryGroupManagement();

  const mobile = isMobile();

  const handleCancelInvite = useCallback(() => {
    modal.confirm({
      content: t('dormitory:messages.confirm-reject-group-invite'),
      onOk: () => rejectInvite(onRefresh),
      okText: t('common:labels.yes'),
      cancelText: t('common:labels.no')
    });
  }, [rejectInvite, onRefresh, modal, t]);

  const handleAcceptInvite = useCallback(() => {
    modal.confirm({
      content: t('dormitory:messages.group-join-confirm'),
      onOk: () => acceptInvite(onRefresh),
      okText: t('common:labels.yes'),
      cancelText: t('common:labels.no')
    });
  }, [onRefresh, modal, t, acceptInvite]);

  const handleLeaveRoom = useCallback(() => {
    modal.confirm({
      content: t('dormitory:messages.confirm-leave-room'),
      okType: 'danger',
      onOk: async () => {
        await removeRoom(group?.dormitoryRoom.id, false);
        onRefresh();
      },
      okText: t('dormitory:labels.leave-room'),
      cancelText: t('common:labels.cancel')
    });
  }, [removeRoom, group, modal, navigate, t, onRefresh]);

  const handleRemoveGroup = useCallback(() => {
    modal.confirm({
      content: t('dormitory:messages.confirm-remove-group'),
      okType: 'danger',
      onOk: async () => {
        await removeGroup();
        message.success(t('dormitory:messages.success-remove-group'));
        onRefresh();
      },
      okText: t('dormitory:labels.remove-group'),
      cancelText: t('common:labels.cancel')
    });
  }, [removeGroup, modal, t, message, navigate, onRefresh]);

  const handleGoToSelectRoom = useCallback(() => {
    navigate(Routes.DormitoryGroupRoomSelector);
  }, [navigate]);

  const me = rows.find(r => r.username === profile?.student?.studentNumber);
  const actionContainerDirection = useMemo(() => mobile ? 'column' : 'row', [mobile]);

  const infoClassName = 'text-base font-semibold inline-flex gap-1';

  const renderedFinal = useMemo(() => {
    return group?.status === 'FINAL' && (
      <Col className="mt-6" span={24}>
        <Flex
          justify="space-around"
          className="w-full text-white p-4 text-base rounded-xl"
          style={{ backgroundColor: colorPrimary }}
        >
          <Flex gap={4} inlineFlex>{t('dormitory:labels.block')}: <span className="font-medium">{group?.dormitoryRoom?.dormitory?.dormitoryBlock?.name}</span></Flex>
          <Flex gap={4} inlineFlex>{t('dormitory:labels.floor')}: <span className="font-medium">{group?.dormitoryRoom?.dormitoryFloor?.name}</span></Flex>
          <Flex gap={4} inlineFlex>{t('dormitory:labels.room')}: <span className="font-medium">{group?.dormitoryRoom?.code}</span></Flex>
        </Flex>
      </Col>
    );
  }, [group, colorPrimary, t]);

  if (solitaryDiff) {
    return (
      <>
        <Col span={24}>
          {group.solitary ?
            t('dormitory:messages.warn-register-solitary-diff') :
            t('dormitory:messages.warn-register-group-diff')
          }
        </Col>
        {renderedFinal}
      </>
    );
  }

  return (
    <>
      {/* Render Waiting */}
      {group?.status === 'WAITING' && (
        <Col span={24}>
          {isHead ? (
            <Flex align="center" direction="row">
              {rows.length < group?.roomSize && (
                <InputPlus
                  maxWidth={350}
                  iconColor={colorPrimary}
                  placeholder={t('common:labels.student-number')}
                  onSubmit={(username) => invite(username, onRefresh)}
                />
              )}
              {!solitaryDiff && <Flex gap={16} className="ms-auto" justify="flex-end">
                {!group?.transferStatus && <AppButton danger onClick={handleRemoveGroup}>
                  {t('common:labels.delete')}
                </AppButton>}
                {!group?.transferStatus && group?.dormitoryRoom?.id &&
                  <AppButton danger onClick={handleLeaveRoom}>{t('dormitory:labels.leave-room')}</AppButton>
                }
              </Flex>}
            </Flex>
          ) : (
            <Flex align="center" direction={actionContainerDirection}>
              {me?.statusRaw === 'APPROVED' ?
                (<div className={infoClassName}>
                  {t('dormitory:messages.wait-approve-members')}
                </div>) :
                <>
                  <div className={infoClassName}>
                    {t('dormitory:messages.confirm-accept-group')}
                  </div>
                  {!solitaryDiff && <Flex gap={16} className="ms-auto">
                    <AppButton onClick={handleCancelInvite} loading={rejecting}>{t('dormitory:labels.cancel-membership')}</AppButton>
                    <AppButton onClick={handleAcceptInvite} loading={accepting} type="primary" appVariant="dormitory">{t('dormitory:labels.accept-membership')}</AppButton>
                  </Flex>}
                </>
              }
            </Flex>
          )}
        </Col>
      )}

      {/* Render Approved */}
      {group?.status === 'APPROVED' && (
        isHead ? (
          <Col span={24}>
            <Flex align="center" direction={actionContainerDirection}>
              <div className={infoClassName}>
                {t('dormitory:messages.success-created-group')} {!group.transferStatus && <>{t('dormitory:messages.you-can-choose-room')}</>}
              </div>
              {!solitaryDiff && <Flex gap={16} className="ms-auto" justify="flex-end">
                {!group?.transferStatus && <AppButton danger onClick={handleRemoveGroup}>{t('dormitory:labels.remove-group')}</AppButton>}
                {group?.dormitoryRoom?.id && <AppButton danger onClick={handleLeaveRoom}>{t('dormitory:labels.leave-room')}</AppButton>}
                {!group?.transferStatus && <AppButton type="primary" appVariant="dormitory" onClick={handleGoToSelectRoom}>{t('dormitory:labels.select-room')}</AppButton>}
              </Flex>}
            </Flex>
          </Col>
        ) :
          <Col span={24}>
            <div className={infoClassName}>
              {t('dormitory:messages.success-created-group')} {t('dormitory:messages.wait-for-select-room')}
            </div>
          </Col>
      )}

      {/* Render Final */}
      {group?.status === 'FINAL' && (
        isHead ? (
          <Col span={24}>
            <Flex align="center" direction={actionContainerDirection}>
              <div className={infoClassName}>
                {group?.transferStatus ? t('dormitory:messages.dormitory-ok-no-transfer') : (
                  <>
                    {t('dormitory:messages.success-created-group')}
                    {t('dormitory:messages.you-can-change-room')}
                  </>
                )}
              </div>
              {!solitaryDiff && !group.transferStatus &&
                <Flex gap={16} className="ms-auto" justify="flex-end">
                  {/* Note: Comment this because requested from product team on date: 1403/06/27  */}
                  {/* {!group?.transferStatus && <AppButton danger onClick={handleRemoveGroup}>{t('common:labels.delete')}</AppButton>} */}
                  {group?.dormitoryRoom?.id && <AppButton danger onClick={handleLeaveRoom}>{t('dormitory:labels.leave-room')}</AppButton>}
                  <AppButton type="primary" appVariant="dormitory" onClick={handleGoToSelectRoom}>{t('dormitory:labels.change-room')}</AppButton>
                </Flex>
              }
            </Flex>
          </Col>
        ) :
          <Col span={24}>
            <div className={infoClassName}>
              {group?.transferStatus ?
                t('dormitory:messages.dormitory-ok-no-transfer') :
                t('dormitory:messages.dormitory-ok')
              }
            </div>
          </Col>
      )}
      {renderedFinal}
    </>
  );
}
